/**
 * "Add Sparrow to your home screen" (docs/mockups/install.html, approach B with C as
 * backup): the in-app way-out card, and the guide sheets the button opens (iPhone,
 * Android without a prompt, the way out, a Mac). Today's own line is TodayInstall.tsx.
 * The rules (when, how often) live in pwa/install-state.ts, the words in pwa/guide.ts,
 * the runtime (held prompt, analytics, carry-over) in pwa/install.ts.
 */
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { androidGuide, exitLink, type Guide, iosGuide, MAC_GUIDE, wayOut } from '../../pwa/guide.ts';
import {
	carriedUrl,
	noteAccepted,
	noteDidntWork,
	noteDismissed,
	noteGuideDone,
	prepareCarry,
	promptNative,
	useInstall,
} from '../../pwa/install.ts';
import type { Surface } from '../../pwa/install-state.ts';
import type { PlatformInfo } from '../../pwa/platform.ts';
import { InstallIcon } from '../icons.tsx';
import { Nib } from '../Nib.tsx';
import { Sheet, SheetTitle } from '../../ui/index.ts';
import { Ill, Pointer, StepText } from './parts.tsx';
import '../../styles/install.css';

// --------------------------------------------------------------------------- the cards

/** In an app's own browser (Instagram, Messenger…): the way out to Safari / Chrome, at the top of Today. */
export function WayOutCard({ platform, surface, onClose }: { platform: PlatformInfo; surface: Surface; onClose: () => void }) {
	const w = wayOut(platform.inApp!, platform.android);
	const exit = useWayOut(surface, platform.android);
	return (
		<section className="icard wayout" aria-labelledby="wayout-t" data-testid="wayout-card">
			<Nib pose="lookup" className="nb" />
			<div>
				<p className="ik">One step first</p>
				<h2 className="it" id="wayout-t">
					{w.title}
				</h2>
				{exit.failed ? (
					<p className="ib step-now">
						<StepText {...w.manual} />
					</p>
				) : (
					<p className="ib">
						{w.why} Or <StepText lead={w.manual.lead.toLowerCase()} glyph={w.manual.glyph} rest={w.manual.rest} /> Today’s solve comes with you.
					</p>
				)}
				<div className="acts">
					<button type="button" className="btn sm primary" onClick={() => void exit.go()}>
						{platform.android ? <InstallIcon.link /> : <InstallIcon.compass />}
						{w.cta}
					</button>
					<button type="button" className="tbtn" onClick={() => void exit.copy()}>
						{exit.copied ? 'Link copied' : 'Copy link'}
					</button>
				</div>
			</div>
			<button type="button" className="x" aria-label="Not now" onClick={onClose}>
				<InstallIcon.close />
			</button>
			{exit.failed && <Pointer at="top_right" />}
		</section>
	);
}

// ------------------------------------------------------------------------- the flows

type SheetKind = 'ios' | 'android' | 'mac' | 'wayout';

/**
 * What "Add to home screen" does here, for one surface: Chrome's own sheet (a held
 * prompt), the iPhone guide (after asking for a carry code), the Android ⋮ guide, the
 * way out of an in-app browser, or File → Add to Dock. `sheet` is the open guide, to be
 * rendered by the caller; `onSettled` hears how a guide ended.
 */
export function useInstallFlow(surface: Surface, onSettled?: (how: 'closed' | 'done') => void) {
	const inst = useInstall();
	const [sheet, setSheet] = useState<SheetKind | null>(null);
	const start = useCallback(() => {
		const route = inst.route;
		if (!route) return;
		noteAccepted(surface, 'cta');
		if (route === 'native') {
			// Chrome's prompt is single-use and can vanish under us: on Android, fall back to the ⋮ guide.
			void promptNative(surface).then((r) => {
				if (r === 'unavailable' && inst.platform.android) setSheet('android');
			});
		} else if (route === 'ios_guide') {
			// The code goes into the address before iOS saves it (pwa/carry.ts).
			void prepareCarry();
			setSheet('ios');
		} else if (route === 'android_guide') setSheet('android');
		else if (route === 'mac_safari') setSheet('mac');
		else setSheet('wayout');
	}, [inst.route, inst.platform.android, surface]);
	const close = (how: 'closed' | 'done') => {
		if (how === 'done') noteGuideDone(surface);
		else noteDismissed(surface, 'guide_closed');
		setSheet(null);
		onSettled?.(how);
	};
	const node = sheet ? <InstallSheet kind={sheet} platform={inst.platform} surface={surface} onClose={close} /> : null;
	return { route: inst.route, platform: inst.platform, installed: inst.installed, start, sheet: node, open: sheet !== null };
}

/** The way out of an in-app browser: try the one-tap exit, else say how (and offer the link). */
function useWayOut(surface: Surface, android: boolean) {
	const [failed, setFailed] = useState(false);
	const [copied, setCopied] = useState(false);
	const code = () => Promise.race([prepareCarry(), new Promise<null>((r) => setTimeout(() => r(null), 1500))]);
	const go = async () => {
		noteAccepted(surface, 'way_out');
		const url = carriedUrl(await code());
		window.setTimeout(() => {
			// Still here a second later: the app didn't let us out. The words say what to do.
			if (document.visibilityState === 'visible') setFailed(true);
		}, 1000);
		try {
			window.location.href = exitLink(url, android);
		} catch {
			setFailed(true);
		}
	};
	const copy = async () => {
		const url = carriedUrl(await code());
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);
		} catch {
			window.prompt('Copy this link, then open it in your browser:', url);
		}
	};
	return { go, copy, failed, copied };
}

// ------------------------------------------------------------------------- the sheet

interface SheetProps {
	kind: SheetKind;
	platform: PlatformInfo;
	surface: Surface;
	onClose: (how: 'closed' | 'done') => void;
}

/**
 * A guide, as a sheet over the page (ui/Sheet): its title in the sheet's heading row, Nib
 * beside the line under it, steps with pictures, and (iPhone) "Found it?" on return. The
 * sheet takes the phone's back, Escape, the dim and a drag down; the page behind it is inert
 * while it's up and focus goes back to the button that opened it after (UX-018), or the
 * page's heading if that's gone (Today's install card). Switching view inside it ("Can't
 * find it?", "Back to the steps") starts the new view from the top (QA2-5).
 */
export function InstallSheet({ kind, platform, surface, onClose }: SheetProps) {
	const [mode, setMode] = useState<'steps' | 'found' | 'all'>('steps');

	// iOS jumps to the home screen after Add; coming back to this tab, the guide asks "Found it?".
	useEffect(() => {
		if (kind !== 'ios') return;
		let away = false;
		const onVis = () => {
			if (document.visibilityState === 'hidden') away = true;
			else if (away) setMode((m) => (m === 'steps' ? 'found' : m));
		};
		document.addEventListener('visibilitychange', onVis);
		return () => document.removeEventListener('visibilitychange', onVis);
	}, [kind]);

	const guide: Guide | null = kind === 'ios' ? iosGuide(platform) : kind === 'android' ? androidGuide(platform) : kind === 'mac' ? MAC_GUIDE : null;
	let title = '';
	let body: ReactNode;
	let pointer: Guide['pointer'] = null;
	if (kind === 'wayout') {
		const w = wayOutWords(platform);
		title = w.title;
		body = <WayOutBody platform={platform} surface={surface} />;
	} else if (mode === 'found') {
		title = 'Found it?';
		body = (
			<>
				<Lead pose="happy" title="Found it?" sub="Open Sparrow from your home screen from now on. Your streak comes with you." />
				<div className="sheet-acts">
					<button type="button" className="btn sm primary" onClick={() => onClose('done')}>
						<InstallIcon.check />
						Done
					</button>
					<button
						type="button"
						className="tbtn"
						onClick={() => {
							noteDidntWork(surface);
							setMode('all');
						}}
					>
						It didn’t work
					</button>
				</div>
				<p className="sheet-foot">Look for the red Sparrow icon on your home screen.</p>
			</>
		);
	} else if (mode === 'all') {
		title = 'Which bar do you have?';
		body = <AllLayouts onDone={() => onClose('done')} onBack={() => setMode('steps')} />;
	} else if (guide) {
		title = guide.title;
		pointer = guide.pointer;
		body = (
			<>
				<Lead pose={kind === 'ios' ? 'dive' : 'lookup'} title={guide.title} sub={guide.sub} />
				<ol className="steps">
					{guide.steps.map((s, i) => (
						<li key={s.lead} className={i === 0 ? 'now' : undefined} aria-current={i === 0 ? 'step' : undefined}>
							<span className="n" aria-hidden="true">
								{i + 1}
							</span>
							<div className="t">
								<StepText lead={s.lead} glyph={s.glyph} rest={s.rest} />
								{s.small && <small>{s.small}</small>}
								{s.ill && (
									<div className="ill">
										<Ill kind={s.ill} />
									</div>
								)}
							</div>
						</li>
					))}
				</ol>
				{(guide.foot || kind === 'ios') && (
					<div className="sheet-foot">
						{guide.foot && (
							<p>
								<StepText {...guide.foot} />
							</p>
						)}
						{kind === 'ios' && (
							<button type="button" className="tbtn" onClick={() => setMode('all')}>
								Can’t find it?
							</button>
						)}
					</div>
				)}
			</>
		);
	}

	const shown = mode === 'steps' ? pointer : null;
	return (
		<Sheet
			open
			title={title}
			// The guide's own heading: Nib beside the big title and its line, ✕ in the corner.
			bareHeader
			onClose={() => onClose('closed')}
			className="guide"
			testId={`sheet-${kind}`}
			// With Nib's arrow at the foot of the screen, the sheet keeps a row for it (install.css).
			data-pointer={shown ?? undefined}
			// Over the sheet, at the browser's own button just off our page.
			outside={
				shown && (
					<div className="guide-pointer" data-testid="guide-pointer">
						<Pointer at={shown} />
					</div>
				)
			}
		>
			{body}
		</Sheet>
	);
}

/** The guide's heading: Nib beside its title (the dialog's name) and the line under it. */
function Lead({ pose, title, sub }: { pose: 'dive' | 'lookup' | 'happy'; title: string; sub: string }) {
	return (
		<div className="hd">
			<Nib pose={pose} className="nb" />
			<div>
				<SheetTitle className="h">{title}</SheetTitle>
				<p className="s">{sub}</p>
			</div>
		</div>
	);
}

/** "It didn't work": every Safari layout, so they can find the one they have. */
function AllLayouts({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
	return (
		<>
			<Lead pose="lookup" title="Which bar do you have?" sub="Safari’s buttons move with its layout. Find yours:" />
			<ul className="layouts">
				<li>
					<Ill kind="bar_more" />
					<p>
						<b>⋯ at the bottom right</b>Tap ⋯, then Share, then Add to Home Screen.
					</p>
				</li>
				<li>
					<Ill kind="bar_share" />
					<p>
						<b>Share in the bottom bar</b>Tap Share, scroll down, then Add to Home Screen.
					</p>
				</li>
				<li>
					<Ill kind="bar_ipad" />
					<p>
						<b>Share at the top (iPad)</b>Tap Share at the top right, then Add to Home Screen.
					</p>
				</li>
			</ul>
			<p className="sheet-foot">In Chrome, Edge or Firefox: Share in the address bar, then Add to Home Screen.</p>
			<div className="sheet-acts">
				<button type="button" className="btn sm primary" onClick={onDone}>
					<InstallIcon.check />
					Done
				</button>
				<button type="button" className="tbtn" onClick={onBack}>
					Back to the steps
				</button>
			</div>
		</>
	);
}

/** The way-out words for this browser (any app's, if it isn't one we know). */
function wayOutWords(platform: PlatformInfo) {
	return wayOut(platform.inApp ?? { id: 'app', name: 'this app', menu: { ios: 'Open in browser', android: 'Open in Chrome' } }, platform.android);
}

function WayOutBody({ platform, surface }: { platform: PlatformInfo; surface: Surface }) {
	const w = wayOutWords(platform);
	const exit = useWayOut(surface, platform.android);
	return (
		<>
			<Lead pose="lookup" title={w.title} sub={`${w.why} Today’s solve comes with you.`} />
			<ol className="steps">
				<li className={exit.failed ? 'now' : undefined}>
					<span className="n" aria-hidden="true">
						1
					</span>
					<div className="t">
						<StepText {...w.manual} />
					</div>
				</li>
			</ol>
			<div className="sheet-acts">
				<button type="button" className="btn sm primary" onClick={() => void exit.go()}>
					{platform.android ? <InstallIcon.link /> : <InstallIcon.compass />}
					{w.cta}
				</button>
				<button type="button" className="tbtn" onClick={() => void exit.copy()}>
					{exit.copied ? 'Link copied' : 'Copy link'}
				</button>
			</div>
		</>
	);
}
